/**
 * ThemeToggle — one button that flips light and dark.
 *
 * It shows the icon of the theme it will switch *to* and names that destination in
 * its accessible label ("Switch to dark theme"), because a lone sun or moon is
 * ambiguous otherwise. Toggling writes an explicit preference, which deliberately
 * ends the `system` follow-along in ThemeProvider — the user has now chosen.
 */
import { forwardRef } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '../../providers/ThemeProvider';
import { Button, type ButtonProps } from '../ui/Button';
import { cn } from '../../lib/cn';

export type ThemeToggleProps = Omit<ButtonProps, 'children' | 'onClick'>;

export const ThemeToggle = forwardRef<HTMLButtonElement, ThemeToggleProps>(function ThemeToggle(
  { className, variant = 'ghost', size = 'icon', ...props },
  ref,
) {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      onClick={toggle}
      aria-label={label}
      title={label}
      className={cn('shrink-0', className)}
      {...props}
    >
      {isDark ? (
        <Sun className="h-5 w-5" aria-hidden="true" />
      ) : (
        <Moon className="h-5 w-5" aria-hidden="true" />
      )}
    </Button>
  );
});
